import { create } from "zustand";
import { assetsApi, genericApi } from "../services/api";

/**
 * API Store for managing assets and categories
 * Handles loading states, errors, and data from backend API.
 * Also provides the assetLoader service (metadata + binary caching) that
 * was previously in services/assetLoader.js.
 */

// Cache for connection check with timestamp
let lastConnectionCheckTime = 0;
const CONNECTION_CHECK_CACHE_TIMEOUT = 10 * 1000; // 10 seconds
let cachedConnectionResult = null;

export const useApiStore = create((set, get) => ({
  // Loading states
  isLoadingAssets: false,
  isLoadingCategories: false,

  // Data
  assets: [],
  categories: [],

  // Error states
  assetsError: null,
  categoriesError: null,

  // Connection state
  isConnected: false,
  connectionError: null,

  /**
   * Check API connection with caching to prevent redundant checks
   * Results are cached for 10 seconds to avoid excessive health checks
   */
  async checkConnection(forceRefresh = false) {
    const now = Date.now();

    // Return cached result if still valid and not forced to refresh
    if (!forceRefresh && cachedConnectionResult !== null && (now - lastConnectionCheckTime) < CONNECTION_CHECK_CACHE_TIMEOUT) {
      return cachedConnectionResult.connected;
    }

    try {
      const result = await genericApi.healthCheck();
      const connected = result && result.status !== 'error';

      // Cache the result
      cachedConnectionResult = { connected, timestamp: now };
      lastConnectionCheckTime = now;

      set({
        isConnected: connected,
        connectionError: connected ? null : result.message
      });
      return connected;
    } catch (error) {
      // Cache error result too
      cachedConnectionResult = { connected: false, timestamp: now };
      lastConnectionCheckTime = now;

      set({
        isConnected: false,
        connectionError: error.message
      });
      return false;
    }
  },

  /**
   * Load assets from API
   */
  async loadAssets(params = {}) {
    set({ isLoadingAssets: true, assetsError: null });

    try {
      const models = await assetsApi.getAvailableModels();
      set({
        assets: models || [],
        isLoadingAssets: false,
        assetsError: null
      });
      return models || [];
    } catch (error) {
      console.error('Failed to load assets:', error);
      set({
        isLoadingAssets: false,
        assetsError: error.message
      });
      throw error;
    }
  },

  /**
   * Load asset categories from API
   */
  async loadCategories() {
    set({ isLoadingCategories: true, categoriesError: null });

    try {
      const categories = await assetsApi.getModelCategories();
      set({
        categories: categories || [],
        isLoadingCategories: false,
        categoriesError: null
      });
      return categories || [];
    } catch (error) {
      console.error('Failed to load categories:', error);
      set({
        isLoadingCategories: false,
        categoriesError: error.message
      });
      // Fallback to default categories
      const fallbackCategories = ['chair', 'couch', 'table', 'lamp', 'plant', 'decoration'];
      set({ categories: fallbackCategories });
      return fallbackCategories;
    }
  },

  /**
   * Reset store to initial state
   */
  reset() {
    set({
      isLoadingAssets: false,
      isLoadingCategories: false,
      assets: [],
      categories: [],
      assetsError: null,
      categoriesError: null,
      isConnected: false,
      connectionError: null
    });
  }
}));

// ---------------------------------------------------------------------------
// Asset Loader — metadata + model binary caching (ported from assetLoader.js)
// ---------------------------------------------------------------------------

// Model binary cache
const modelCache = new Map();
const loadingPromises = new Map();

// Metadata cache
let availableModels = null;
let modelCategories = null;
let hierarchicalCategories = null;
let lastFetchTime = null;

// Cache timeout — 5 minutes
const CACHE_TIMEOUT = 5 * 60 * 1000;

// Micro-cache for isCacheValid (avoids repeated Date.now() calls)
let cachedValidation = { valid: false, timestamp: 0 };
const VALIDATION_CACHE_TIMEOUT = 1000; // 1 second

const LOADING_STATUS = {
  NOT_LOADED: 'not_loaded',
  LOADING: 'loading',
  LOADED: 'loaded',
  ERROR: 'error'
};

const isCacheValid = () => {
  const now = Date.now();
  if (now - cachedValidation.timestamp < VALIDATION_CACHE_TIMEOUT) {
    return cachedValidation.valid;
  }
  const valid = lastFetchTime && (now - lastFetchTime) < CACHE_TIMEOUT;
  cachedValidation = { valid, timestamp: now };
  return valid;
};

export const assetLoader = {
  /**
   * Initialize the asset loader by fetching available models.
   * Uses smart caching to avoid unnecessary API calls.
   */
  initialize: async (forceRefresh = false) => {
    try {
      const shouldFetch = forceRefresh || !availableModels || !isCacheValid();

      if (shouldFetch) {
        console.log('AssetLoader: Fetching fresh data from API...');

        try {
          const assetsData = await assetsApi.getAssetsWithHierarchy();

          if (assetsData && assetsData.hierarchicalCategories) {
            console.log('AssetLoader: Using hierarchical category structure');
            hierarchicalCategories = assetsData.hierarchicalCategories;
            availableModels = assetsData.models || [];
            modelCategories = assetsData.categories || [];
          } else {
            console.log('AssetLoader: Falling back to flat category structure');
            const [models, categories] = await Promise.allSettled([
              assetsApi.getAvailableModels(),
              assetsApi.getModelCategories()
            ]);

            availableModels = models.status === 'fulfilled' ? (models.value || []) : [];
            modelCategories = categories.status === 'fulfilled' ? (categories.value || []) : [];
            hierarchicalCategories = null;
          }
        } catch (apiError) {
          console.warn('AssetLoader: API call failed, using empty data:', apiError.message);
          availableModels = [];
          modelCategories = [];
          hierarchicalCategories = null;
        }

        lastFetchTime = Date.now();
        console.log(`AssetLoader: Cached ${availableModels?.length || 0} models`);
      } else {
        console.log('AssetLoader: Using cached data (still valid)');
      }

      return true;
    } catch (error) {
      console.error('AssetLoader: Failed to initialize:', error);
      return false;
    }
  },

  /** Get available models (cached) */
  getAvailableModels: async () => {
    if (!availableModels) {
      await assetLoader.initialize();
    }
    return availableModels || [];
  },

  /** Get model categories (cached) */
  getModelCategories: async () => {
    if (!modelCategories) {
      await assetLoader.initialize();
    }
    return modelCategories || [];
  },

  /** Get hierarchical categories (cached) */
  getHierarchicalCategories: async () => {
    if (!hierarchicalCategories) {
      await assetLoader.initialize();
    }
    return hierarchicalCategories || null;
  },

  /** Get model by ID */
  getModelById: async (modelId) => {
    const models = await assetLoader.getAvailableModels();
    return models.find(model => model.id === modelId);
  },

  /** Get models by category */
  getModelsByCategory: async (category) => {
    const models = await assetLoader.getAvailableModels();
    return models.filter(model => model.category === category);
  },

  /** Get the presigned URL for a model */
  getModelUrl: async (modelPath) => {
    return await assetsApi.getModelUrl(modelPath);
  },

  /**
   * Get presigned thumbnail URL for a model
   */
  getThumbnailPresignedUrl: async (modelOrPath) => {
    try {
      let modelId;
      if (typeof modelOrPath === 'object') {
        modelId = modelOrPath.id || modelOrPath.assetId;
      } else {
        modelId = modelOrPath;
      }

      if (!modelId) {
        console.warn('No model ID provided for thumbnail presigned URL');
        return null;
      }

      const response = await assetsApi.getModelWithThumbnailUrls(modelId);
      return response.thumbnailUrl || null;
    } catch (error) {
      console.error('Failed to get thumbnail presigned URL:', error);
      return null;
    }
  },

  /**
   * Get the thumbnail URL for a model
   */
  getThumbnailUrl: async (modelOrPath) => {
    try {
      if (typeof modelOrPath === 'object' && modelOrPath.thumbnailUrl) {
        return modelOrPath.thumbnailUrl;
      }
      if (typeof modelOrPath === 'object' && modelOrPath.thumbnailS3Key) {
        return modelOrPath.thumbnailUrl;
      }
      return assetsApi.getThumbnailUrl(modelOrPath);
    } catch (error) {
      console.error('Failed to get thumbnail URL:', error);
      return null;
    }
  },

  /** Preload a model to cache it */
  preloadModel: async (modelPath) => {
    const cacheKey = modelPath;

    if (modelCache.has(cacheKey)) {
      const cached = modelCache.get(cacheKey);
      if (cached.status === LOADING_STATUS.LOADED) {
        return { success: true, cached: true };
      }
      if (cached.status === LOADING_STATUS.ERROR) {
        return { success: false, error: cached.error, cached: true };
      }
    }

    if (loadingPromises.has(cacheKey)) {
      try {
        const result = await loadingPromises.get(cacheKey);
        return result;
      } catch (error) {
        return { success: false, error };
      }
    }

    const loadingPromise = assetLoader._loadModel(modelPath);
    loadingPromises.set(cacheKey, loadingPromise);

    try {
      const result = await loadingPromise;
      loadingPromises.delete(cacheKey);
      return result;
    } catch (error) {
      loadingPromises.delete(cacheKey);
      throw error;
    }
  },

  /** Internal method to actually load a model */
  _loadModel: async (modelPath) => {
    const cacheKey = modelPath;
    const url = await assetLoader.getModelUrl(modelPath);

    if (!url) {
      throw new Error('No URL available for model');
    }

    modelCache.set(cacheKey, {
      status: LOADING_STATUS.LOADING,
      url,
      loadedAt: null,
      error: null
    });

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const blob = await response.blob();

      modelCache.set(cacheKey, {
        status: LOADING_STATUS.LOADED,
        url,
        blob,
        loadedAt: new Date(),
        error: null,
        size: blob.size
      });

      return { success: true, url, size: blob.size, cached: false };
    } catch (error) {
      console.error('Failed to preload model:', modelPath, error);
      modelCache.set(cacheKey, {
        status: LOADING_STATUS.ERROR,
        url,
        loadedAt: null,
        error: error.message
      });
      return { success: false, error: error.message, cached: false };
    }
  },

  /** Preload multiple models in parallel */
  preloadModels: async (modelPaths, options = {}) => {
    const { maxConcurrent = 5, onProgress } = options;
    const results = [];
    const batches = [];

    for (let i = 0; i < modelPaths.length; i += maxConcurrent) {
      batches.push(modelPaths.slice(i, i + maxConcurrent));
    }

    let completedCount = 0;

    const processModel = async (modelPath) => {
      try {
        const result = await assetLoader.preloadModel(modelPath);
        completedCount++;
        if (onProgress) {
          onProgress({ completed: completedCount, total: modelPaths.length, current: modelPath, result });
        }
        return { modelPath, ...result };
      } catch (error) {
        completedCount++;
        const errorResult = { modelPath, success: false, error: error.message };
        if (onProgress) {
          onProgress({ completed: completedCount, total: modelPaths.length, current: modelPath, result: errorResult });
        }
        return errorResult;
      }
    };

    for (const batch of batches) {
      const batchResults = await Promise.all(batch.map(processModel));
      results.push(...batchResults);
    }

    return results;
  },

  /** Get cached model data */
  getCachedModel: (modelPath) => {
    return modelCache.get(modelPath) || null;
  },

  /** Check if a model is cached */
  isModelCached: (modelPath) => {
    const cached = modelCache.get(modelPath);
    return cached?.status === LOADING_STATUS.LOADED;
  },

  /** Get cache statistics */
  getCacheStats: () => {
    const stats = {
      totalModels: modelCache.size,
      loadedModels: 0,
      loadingModels: 0,
      errorModels: 0,
      totalSize: 0,
      oldestEntry: null,
      newestEntry: null
    };

    for (const [, data] of modelCache.entries()) {
      switch (data.status) {
        case LOADING_STATUS.LOADED:
          stats.loadedModels++;
          if (data.size) stats.totalSize += data.size;
          if (!stats.oldestEntry || data.loadedAt < stats.oldestEntry) stats.oldestEntry = data.loadedAt;
          if (!stats.newestEntry || data.loadedAt > stats.newestEntry) stats.newestEntry = data.loadedAt;
          break;
        case LOADING_STATUS.LOADING:
          stats.loadingModels++;
          break;
        case LOADING_STATUS.ERROR:
          stats.errorModels++;
          break;
        default:
          break;
      }
    }

    return stats;
  },

  /** Clear cache */
  clearCache: (olderThan = null) => {
    if (olderThan) {
      for (const [key, data] of modelCache.entries()) {
        if (data.loadedAt && data.loadedAt < olderThan) {
          modelCache.delete(key);
        }
      }
    } else {
      modelCache.clear();
      availableModels = null;
      modelCategories = null;
      hierarchicalCategories = null;
      lastFetchTime = null;
      cachedValidation = { valid: false, timestamp: 0 };
      console.log('AssetLoader: All caches cleared');
    }
  },

  /** Get loading status for a model */
  getLoadingStatus: (modelPath) => {
    const cached = modelCache.get(modelPath);
    return cached?.status || LOADING_STATUS.NOT_LOADED;
  },

  /** Cleanup old cache entries (LRU-like behavior) */
  cleanup: (maxEntries = 100, maxAge = 30 * 60 * 1000) => {
    const now = new Date();
    const entries = Array.from(modelCache.entries());

    const validEntries = entries.filter(([, data]) => {
      if (!data.loadedAt) return true;
      return (now - data.loadedAt) < maxAge;
    });

    if (validEntries.length > maxEntries) {
      validEntries.sort(([,a], [,b]) => {
        if (!a.loadedAt) return -1;
        if (!b.loadedAt) return 1;
        return b.loadedAt - a.loadedAt;
      });

      const toKeep = validEntries.slice(0, maxEntries);
      modelCache.clear();
      for (const [key, data] of toKeep) {
        modelCache.set(key, data);
      }
    }
  }
};

// Auto-cleanup every 5 minutes (aligned with cache timeout)
setInterval(() => {
  assetLoader.cleanup();
}, 5 * 60 * 1000);
